import { fieldsMatch, isRecord, isStringOrNull } from "@/lib/ui/validatedJson";

export type ShowCatalogCard = {
  card_id: string;
  name: string;
  image_url: string | null;
  game: string | null;
  set_name: string | null;
  collector_number: string | null;
  printing: string | null;
  edition: string | null;
  language: string | null;
  vendor_count: number;
  quantity: number;
};

const count = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

/** Shared boundary check for live Show responses and their on-device Hunt route snapshots. */
export function isShowCatalogCard(value: unknown): value is ShowCatalogCard {
  return isRecord(value) && typeof value.card_id === "string" && typeof value.name === "string"
    && fieldsMatch(
      value,
      ["image_url", "game", "set_name", "collector_number", "printing", "edition", "language"],
      isStringOrNull,
    )
    && count(value.vendor_count) && count(value.quantity);
}

export type ShowCardListing = {
  card: Omit<ShowCatalogCard, "quantity" | "vendor_count">;
  participationId: string;
  quantity: number;
};

export type ShowCardCopy = {
  condition: string | null;
  quantity: number;
  is_graded: boolean;
  grading_company: string | null;
  grade: string | null;
};

export type ShowCardVendor = {
  participation_id: string;
  name: string;
  table_numbers: string[];
  copies: ShowCardCopy[];
};

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];
const tableOrder = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

const conditionRank = (condition: string | null): number => {
  const rank = condition ? CONDITIONS.indexOf(condition.toUpperCase()) : -1;
  return rank < 0 ? CONDITIONS.length : rank;
};

/** One Show-wide row per catalog identity; treatment stays encoded in `card_id`. */
export function groupCatalogCardListings(listings: readonly ShowCardListing[]): ShowCatalogCard[] {
  const groups = new Map<
    string,
    { card: ShowCardListing["card"]; vendors: Set<string>; quantity: number; }
  >();
  for (const listing of listings) {
    const group = groups.get(listing.card.card_id) ?? {
      card: listing.card,
      vendors: new Set<string>(),
      quantity: 0,
    };
    group.vendors.add(listing.participationId);
    group.quantity += listing.quantity;
    groups.set(listing.card.card_id, group);
  }
  return [...groups.values()].map(({ card, vendors, quantity }) => ({
    ...card,
    quantity,
    vendor_count: vendors.size,
  }));
}

export function availabilityLabel(vendorCount: number, quantity: number): string {
  return `${vendorCount} ${vendorCount === 1 ? "vendor" : "vendors"} · ${quantity} ${
    quantity === 1 ? "copy" : "copies"
  }`;
}

export function treatmentLabel(card: ShowCatalogCard): string | null {
  const parts = [card.printing, card.edition, card.language]
    .map((part) => part?.trim())
    .filter((part, index, all): part is string => !!part && all.indexOf(part) === index);
  return parts.length
    ? parts.map((part) =>
      part.replaceAll(/[-_]+/g, " ").replaceAll(/\b\w/g, (letter) => letter.toUpperCase())
    ).join(" · ")
    : null;
}

export function sortCardCopies(copies: readonly ShowCardCopy[]): ShowCardCopy[] {
  return [...copies].sort((a, b) =>
    conditionRank(a.condition) - conditionRank(b.condition)
    || (a.grade ?? "").localeCompare(b.grade ?? "")
  );
}

/** Best condition first, then walking order; copies use the same condition ladder. */
export function sortCardVendors(vendors: readonly ShowCardVendor[]): ShowCardVendor[] {
  return vendors
    .map((vendor) => ({
      ...vendor,
      copies: sortCardCopies(vendor.copies),
      table_numbers: [...vendor.table_numbers].sort((a, b) => tableOrder.compare(a, b)),
    }))
    .sort((a, b) =>
      conditionRank(a.copies[0]?.condition ?? null)
        - conditionRank(b.copies[0]?.condition ?? null)
      || tableOrder.compare(a.table_numbers[0] ?? "", b.table_numbers[0] ?? "")
      || a.name.localeCompare(b.name)
    );
}
